import React, { useEffect, useState, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { authAPI, vehiclesAPI } from '../api/api';
import '../css/Documents.css';
import { usePermissions } from '../hooks/usePermissions';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function downloadBase64File(doc) {
  if (!doc.content) {
    alert('A fájl tartalma nem érhető el');
    return;
  }
  const binaryString = window.atob(doc.content);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const blob = new Blob(
    [bytes],
    { type: doc.mime || 'application/octet-stream' }
  );
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = doc.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function fetchPhoto(photoId) {
  const token = localStorage.getItem('authToken');
  return fetch(
    `${API_BASE_URL}/get-photo/${photoId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  )
    .then(res => res.json())
    .then(result => {
      if (result.success) return result.data;
      return null;
    })
    .catch(() => null);
}

function GalleryLightbox({ gallery, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [photoCache, setPhotoCache] = useState({});
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const photos = gallery.photos || [];

  const loadPhoto = useCallback((index) => {
    const photo = photos[index];
    if (!photo || photoCache[photo.id]) return;

    setLoadingPhoto(true);
    fetchPhoto(photo.id).then(data => {
      if (data) {
        setPhotoCache(prev => ({
          ...prev,
          [photo.id]: data,
        }));
      }
      setLoadingPhoto(false);
    });
  }, [photos, photoCache]);

  useEffect(() => {
    loadPhoto(currentIndex);
  }, [currentIndex, loadPhoto]);

  useEffect(() => {
    if (currentIndex < photos.length - 1) {
      loadPhoto(currentIndex + 1);
    }
  }, [currentIndex, photos.length, loadPhoto]);

  const goNext = useCallback(() => {
    if (currentIndex < photos.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, photos.length]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [goNext, goPrev, onClose]);

  const current = photos[currentIndex];
  const cached = current ? photoCache[current.id] : null;

  return (
    <div
      className="gallery-lightbox-overlay"
      onClick={onClose}
    >
      <div
        className="gallery-lightbox-content"
        onClick={e => e.stopPropagation()}
      >
        <button
          className="gallery-lightbox-close"
          onClick={onClose}
        >
          &times;
        </button>
        {loadingPhoto && !cached ? (
          <div className="gallery-lightbox-placeholder">
            Betöltés...
          </div>
        ) : cached ? (
          <img
            src={
              `data:image/${cached.extension}`
              + `;base64,${cached.content}`
            }
            alt={cached.filename}
            className="gallery-lightbox-img"
          />
        ) : (
          <div className="gallery-lightbox-placeholder">
            {current?.filename || 'Kép nem elérhető'}
          </div>
        )}
        <div className="gallery-lightbox-caption">
          {current?.filename}
          <span className="gallery-lightbox-counter">
            {currentIndex + 1} / {photos.length}
          </span>
        </div>
        <div className="gallery-lightbox-nav">
          <button
            className="gallery-lightbox-nav-btn"
            onClick={goPrev}
            disabled={currentIndex === 0}
          >
            &lsaquo;
          </button>
          <button
            className="gallery-lightbox-nav-btn"
            onClick={goNext}
            disabled={currentIndex === photos.length - 1}
          >
            &rsaquo;
          </button>
        </div>
      </div>
    </div>
  );
}

const Documents = ({ isSidebarClosed, setIsSidebarClosed }) => {
  const [documents, setDocuments] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [archivedDocuments, setArchivedDocuments] = useState([]);
  const [search, setSearch] = useState('');
  const [user, setUser] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeGallery, setActiveGallery] = useState(null);
  const { permissions } = usePermissions();

  const handleDownload = (e, doc) => {
    e.preventDefault();
    try {
      downloadBase64File(doc);
    } catch (error) {
      console.error('Download error:', error);
      alert('Hiba a fájl letöltésekor');
    }
  };

  const handleZipDownload = (e, gallery) => {
    e.preventDefault();
    const token = localStorage.getItem('authToken');
    const url = `${API_BASE_URL}${gallery.download_zip_url}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `${gallery.name}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => alert('Hiba a ZIP letöltésekor'));
  };

  useEffect(() => {
    const currentUser = authAPI.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      fetchDocuments(currentUser);
    }
  }, []);

  const fetchDocuments = async (currentUser) => {
    try {
      setLoading(true);
      let vehiclesData;

      if (
        currentUser.role === 'admin'
        || currentUser.role === 'boss'
      ) {
        vehiclesData = await vehiclesAPI.getVehiclesByClient(
          currentUser.client_id || currentUser.company
        );
      } else {
        vehiclesData = await vehiclesAPI.getVehiclesByOwner(
          currentUser.name,
          currentUser.client_id || currentUser.company
        );
      }

      const docs = [];
      const galleryItems = [];

      for (const vehicle of vehiclesData) {
        if (!vehicle.id) continue;

        try {
          const vehicleDocsData =
            await vehiclesAPI.getVehicleDocuments(vehicle.id);

          if (!vehicleDocsData.sections) continue;

          const vehicleInfo = {
            brand: vehicle.brand,
            model: vehicle.model,
            plate_number: vehicle.plate_number,
            chassis_number: vehicle.chassis_number,
            chassis_number17: vehicle.chassis_number17,
            owner: vehicle.owner,
          };

          Object.entries(vehicleDocsData.sections).forEach(
            ([sectionKey, section]) => {
              section.files.forEach(file => {
                docs.push({
                  id: file.id,
                  name: file.filename
                    + (file.extension
                      ? `.${file.extension}`
                      : ''),
                  filename: file.filename,
                  extension: file.extension,
                  mime: file.mime,
                  content: file.content,
                  section_name: section.name,
                  uploaded_at: file.uploaded_at,
                  ...vehicleInfo,
                  archived: false,
                });
              });

              if (section.photo_galleries) {
                section.photo_galleries.forEach(gallery => {
                  galleryItems.push({
                    ...gallery,
                    section_name: section.name,
                    ...vehicleInfo,
                  });
                });
              }
            }
          );
        } catch (error) {
          console.error(
            `Error fetching docs for vehicle ${vehicle.id}:`,
            error
          );
        }
      }

      setDocuments(docs);
      setGalleries(galleryItems);
      setArchivedDocuments([]);
    } catch (error) {
      console.error('Hiba a dokumentumok lekérésekor:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentDocuments = showArchived
    ? archivedDocuments
    : documents;

  const filteredDocuments = currentDocuments.filter(doc => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (doc.brand && doc.brand.toLowerCase().includes(q))
      || (doc.model && doc.model.toLowerCase().includes(q))
      || (doc.plate_number
        && doc.plate_number.toLowerCase().includes(q))
      || (doc.chassis_number
        && doc.chassis_number.toLowerCase().includes(q))
      || (doc.chassis_number17
        && doc.chassis_number17.toLowerCase().includes(q))
      || (doc.owner && doc.owner.toLowerCase().includes(q))
      || (doc.section_name
        && doc.section_name.toLowerCase().includes(q))
      || (doc.name && doc.name.toLowerCase().includes(q))
    );
  });

  const filteredGalleries = galleries.filter(g => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (g.brand && g.brand.toLowerCase().includes(q))
      || (g.model && g.model.toLowerCase().includes(q))
      || (g.plate_number
        && g.plate_number.toLowerCase().includes(q))
      || (g.name && g.name.toLowerCase().includes(q))
    );
  });

  const vehicleGroups = {};

  const ensureVehicleGroup = (item) => {
    const key = item.chassis_number17 || item.chassis_number;
    if (!vehicleGroups[key]) {
      vehicleGroups[key] = {
        brand: item.brand,
        model: item.model,
        plate_number: item.plate_number,
        chassis_number: item.chassis_number,
        chassis_number17: item.chassis_number17,
        documents: [],
        galleries: [],
      };
    }
    return key;
  };

  filteredDocuments.forEach(doc => {
    const key = ensureVehicleGroup(doc);
    vehicleGroups[key].documents.push(doc);
  });

  filteredGalleries.forEach(gallery => {
    const key = ensureVehicleGroup(gallery);
    vehicleGroups[key].galleries.push(gallery);
  });

  const totalItemCount = (vehicle) =>
    vehicle.documents.length + vehicle.galleries.length;

  if (!permissions.can_see_documents) {
    return (
      <div>
        <Navbar
          isSidebarClosed={isSidebarClosed}
          setIsSidebarClosed={setIsSidebarClosed}
        />
        <Sidebar
          isCollapsed={isSidebarClosed}
          setIsCollapsed={setIsSidebarClosed}
        />
        <div className="page-content">
          <div style={{
            padding: 32,
            textAlign: 'center',
            fontSize: '1.2rem',
            color: '#721c24',
            background: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '6px',
            margin: '20px',
          }}>
            Nincs jogosultságod a dokumentumok megtekintéséhez.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar
        isSidebarClosed={isSidebarClosed}
        setIsSidebarClosed={setIsSidebarClosed}
      />
      <Sidebar
        isCollapsed={isSidebarClosed}
        setIsCollapsed={setIsSidebarClosed}
      />
      <div className="page-content documents-page">
        <div className="documents-header">
          <h2 className="documents-title">
            {showArchived
              ? 'Archív Dokumentumok'
              : 'Dokumentumok'}
          </h2>
          <button
            className="documents-toggle-btn"
            onClick={() => setShowArchived(!showArchived)}
          >
            {showArchived
              ? 'Aktív Dokumentumok'
              : 'Archív Dokumentumok'}
          </button>
        </div>
        <div className="documents-search-row">
          <input
            className="documents-search-input"
            placeholder="Keresés..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="documents-content">
          {loading ? (
            <div className="documents-empty">
              Dokumentumok betöltése...
            </div>
          ) : Object.keys(vehicleGroups).length === 0 ? (
            <div className="documents-empty">
              {showArchived
                ? 'Nincs archív dokumentum.'
                : 'Nincs dokumentum.'}
            </div>
          ) : (
            <div className="vehicle-documents-grid">
              {Object.entries(vehicleGroups).map(
                ([vehicleKey, vehicle]) => (
                  <div
                    key={vehicleKey}
                    className="vehicle-document-card"
                  >
                    <div className="vehicle-card-header">
                      <h3 className="vehicle-card-title">
                        {vehicle.brand} {vehicle.model}
                      </h3>
                      <div className="vehicle-card-info">
                        <span className="vehicle-info-item">
                          <strong>Rendszám:</strong>{' '}
                          {vehicle.plate_number || '---'}
                        </span>
                        <span className="vehicle-info-item">
                          <strong>Alvázszám:</strong>{' '}
                          {vehicle.chassis_number17}
                        </span>
                        <span className="vehicle-info-badge">
                          {totalItemCount(vehicle)} dokumentum
                        </span>
                      </div>
                    </div>
                    <div className="vehicle-card-body">
                      <table className="vehicle-documents-table">
                        <thead>
                          <tr>
                            <th>Típus</th>
                            <th>Dokumentum neve</th>
                            <th>Dátum</th>
                            <th>Műveletek</th>
                          </tr>
                        </thead>
                        <tbody>
                          {vehicle.galleries.map(
                            (gallery, gIdx) => (
                              <tr
                                key={
                                  `g-${gallery.requirement_id}`
                                  + `-${gallery.rental_id}`
                                  + `-${gIdx}`
                                }
                              >
                                <td data-label="Típus">
                                  {gallery.section_name}
                                </td>
                                <td data-label="Dokumentum neve">
                                  {gallery.name}
                                  {' '}
                                  <span
                                    className="gallery-row-badge"
                                  >
                                    {gallery.photo_count} fotó
                                  </span>
                                </td>
                                <td data-label="Dátum">
                                  {gallery.uploaded_at
                                    ? gallery.uploaded_at
                                      .substring(0, 16)
                                    : ''}
                                </td>
                                <td data-label="Műveletek">
                                  <div
                                    className="gallery-row-actions"
                                  >
                                    <button
                                      className={
                                        'gallery-action-btn'
                                        + ' gallery-view-btn'
                                      }
                                      onClick={
                                        () => setActiveGallery(
                                          gallery
                                        )
                                      }
                                      title="Fotók megtekintése"
                                    >
                                      <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                      </svg>
                                    </button>
                                    <button
                                      className={
                                        'gallery-action-btn'
                                        + ' gallery-download-btn'
                                      }
                                      onClick={
                                        (e) => handleZipDownload(
                                          e, gallery
                                        )
                                      }
                                      title="ZIP letöltés"
                                    >
                                      <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7 10 12 15 17 10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )
                          )}
                          {vehicle.documents.map((doc, idx) => (
                            <tr key={doc.id + '-' + idx}>
                              <td data-label="Típus">
                                {doc.section_name}
                              </td>
                              <td data-label="Dokumentum neve">
                                {doc.name}
                              </td>
                              <td data-label="Dátum">
                                {doc.uploaded_at
                                  ? doc.uploaded_at.substring(0, 16)
                                  : ''}
                              </td>
                              <td data-label="Műveletek">
                                <button
                                  onClick={
                                    (e) => handleDownload(e, doc)
                                  }
                                  className="documents-download-btn"
                                >
                                  Letöltés
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {activeGallery && (
        <GalleryLightbox
          gallery={activeGallery}
          onClose={() => setActiveGallery(null)}
        />
      )}
    </div>
  );
};

export default Documents;
